import { Injectable } from '@angular/core';

declare global {
  interface Window {
    swbrowser?: BrowserCapabilities;
    xbrowser?: BrowserCapabilities;
    Frames?: unknown;
  }
}

/** Local Window shape for Turnstile — keep out of global Window to avoid merge conflicts. */
type TurnstileWindow = Window & {
  turnstile?: unknown;
  onloadTurnstileCallback?: () => void;
};

interface BrowserCapabilities {
  isModern: boolean;
  supportsCSS: boolean;
  userAgent: string;
  isChrome: boolean;
  isFirefox: boolean;
  isSafari: boolean;
  isEdge: boolean;
  isIE: boolean;
}

const CHECKOUT_FRAMES_URL = 'https://cdn.checkout.com/js/framesv2.min.js';
const JQUERY_URL = 'https://code.jquery.com/jquery-3.7.1.min.js';
const TURNSTILE_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback';

@Injectable({
  providedIn: 'root'
})
export class ResourceLoaderService {
  private checkoutFramesPromise: Promise<void> | null = null;
  private turnstilePromise: Promise<void> | null = null;
  private checkoutPolyfillsInstalled = false;

  public async loadCheckoutFrames(): Promise<void> {
    if (this.isCheckoutFramesReady()) {
      return Promise.resolve();
    }
    if (this.checkoutFramesPromise) {
      return this.checkoutFramesPromise;
    }

    this.checkoutFramesPromise = this.loadCheckoutFramesInternal().catch((error) => {
      this.checkoutFramesPromise = null;
      throw error;
    });

    return this.checkoutFramesPromise;
  }

  public async loadTurnstile(): Promise<void> {
    const win = window as TurnstileWindow;
    if (typeof win.turnstile !== 'undefined') {
      return Promise.resolve();
    }
    if (this.turnstilePromise) {
      return this.turnstilePromise;
    }

    this.turnstilePromise = this.loadTurnstileInternal().catch((error) => {
      this.turnstilePromise = null;
      throw error;
    });

    return this.turnstilePromise;
  }

  private async loadCheckoutFramesInternal(): Promise<void> {
    this.installCheckoutPolyfills();

    if (!this.scriptPresent(JQUERY_URL)) {
      await this.loadScriptsSequentially([
        {
          url: JQUERY_URL,
          attributes: {
            integrity: 'sha256-/JqT3SQfawRcv/BIHPThkBvs0OEvtFFmqPF/lYI/Cxo=',
            crossorigin: 'anonymous',
          },
        },
      ]);
    }

    if (!this.scriptPresent(CHECKOUT_FRAMES_URL)) {
      await this.loadScriptsSequentially([{ url: CHECKOUT_FRAMES_URL }]);
    }

    if (!this.isCheckoutFramesReady()) {
      throw new Error('Checkout Frames API not available after load');
    }
  }

  private async loadTurnstileInternal(): Promise<void> {
    const win = window as TurnstileWindow;
    if (this.scriptPresent('challenges.cloudflare.com/turnstile')) {
      await this.waitForTurnstileApi();
      return;
    }

    const previousCallback = win.onloadTurnstileCallback;

    await new Promise<void>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        reject(new Error('Turnstile load timed out'));
      }, 15000);

      win.onloadTurnstileCallback = () => {
        window.clearTimeout(timeoutId);
        try {
          previousCallback?.();
        } catch {
          // A consumer callback failure must not block script readiness.
        }
        resolve();
      };

      const script = document.createElement('script');
      script.src = TURNSTILE_URL;
      script.defer = true;
      script.onerror = () => {
        window.clearTimeout(timeoutId);
        reject(new Error('Failed to load Turnstile script'));
      };
      document.head.appendChild(script);
    });

    if (!win.turnstile) {
      await this.waitForTurnstileApi();
    }
  }

  private async waitForTurnstileApi(): Promise<void> {
    const win = window as TurnstileWindow;
    const started = Date.now();
    while (typeof win.turnstile === 'undefined') {
      if (Date.now() - started > 15000) {
        throw new Error('Turnstile API not available after load');
      }
      await new Promise((resolve) => window.setTimeout(resolve, 50));
    }
  }

  private isCheckoutFramesReady(): boolean {
    return typeof window.Frames !== 'undefined';
  }

  private installCheckoutPolyfills(): void {
    if (this.checkoutPolyfillsInstalled) {
      return;
    }
    this.checkoutPolyfillsInstalled = true;
    this.initializeBrowserDetectionVars();
    this.installStyleSheetProtectionPolyfill();
  }

  private initializeBrowserDetectionVars(): void {
    try {
      if (typeof window.swbrowser === 'undefined') {
        window.swbrowser = this.detectBrowserCapabilities();
      }
      if (typeof window.xbrowser === 'undefined') {
        window.xbrowser = this.detectBrowserCapabilities();
      }
    } catch {
      // Non-critical: frames may still load on capable browsers.
    }
  }

  private detectBrowserCapabilities(): BrowserCapabilities {
    const userAgent = navigator.userAgent.toLowerCase();
    return {
      isModern: true,
      supportsCSS: true,
      userAgent,
      isChrome: userAgent.indexOf('chrome') > -1 && userAgent.indexOf('edge') === -1,
      isFirefox: userAgent.indexOf('firefox') > -1,
      isSafari: userAgent.indexOf('safari') > -1 && userAgent.indexOf('chrome') === -1,
      isEdge: userAgent.indexOf('edge') > -1 || userAgent.indexOf('edg/') > -1,
      isIE: userAgent.indexOf('msie') > -1 || userAgent.indexOf('trident/') > -1,
    };
  }

  private installStyleSheetProtectionPolyfill(): void {
    try {
      if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
      }

      const createSafeStyleSheetProxy = (styleSheet: CSSStyleSheet | null): CSSStyleSheet | null => {
        if (!styleSheet) {
          return null;
        }

        return new Proxy(styleSheet, {
          get(target, prop) {
            if (prop === 'cssRules') {
              try {
                return target.cssRules;
              } catch {
                return [];
              }
            }

            const value = target[prop as keyof CSSStyleSheet];
            return typeof value === 'function' ? value.bind(target) : value;
          }
        });
      };

      const originalStyleSheets = document.styleSheets;

      Object.defineProperty(document, 'styleSheets', {
        get() {
          try {
            const styleSheetList = originalStyleSheets;

            return new Proxy(styleSheetList, {
              get(target, prop) {
                if (typeof prop === 'string' && /^\d+$/.test(prop)) {
                  const index = parseInt(prop, 10);
                  return createSafeStyleSheetProxy(target[index]);
                }

                if (prop === 'item') {
                  return function(index: number) {
                    return createSafeStyleSheetProxy(target.item(index));
                  };
                }

                if (prop === Symbol.iterator) {
                  return function* () {
                    for (let i = 0; i < target.length; i++) {
                      yield createSafeStyleSheetProxy(target[i]);
                    }
                  };
                }

                const value = target[prop as keyof StyleSheetList];
                return typeof value === 'function' ? value.bind(target) : value;
              }
            });
          } catch {
            return originalStyleSheets;
          }
        },
        configurable: true,
        enumerable: true
      });
    } catch {
      // Non-critical polyfill failure.
    }
  }

  private scriptPresent(urlFragment: string): boolean {
    return Array.from(document.querySelectorAll('script[src]')).some((node) => {
      const src = (node as HTMLScriptElement).src;
      return src.includes(urlFragment);
    });
  }

  private async loadScriptsSequentially(
    scripts: { url: string; attributes?: Record<string, string> }[]
  ): Promise<void> {
    for (const { url, attributes } of scripts) {
      if (this.scriptPresent(url)) {
        continue;
      }

      await new Promise<void>((resolve, reject) => {
        try {
          const script = document.createElement('script');
          script.src = url;

          if (attributes) {
            Object.entries(attributes).forEach(([key, value]) =>
              script.setAttribute(key, value)
            );
          }

          script.onload = () => resolve();
          script.onerror = () => reject(new Error(`Failed to load script: ${url}`));

          document.head.appendChild(script);
        } catch (error) {
          reject(error);
        }
      });
    }
  }
}
